import { useEffect, useMemo, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { fmtBRL, fmtNum, fmtDate } from "@/lib/format";
import type { NotaSimplificada } from "@/lib/fiscal-types";
import {
  ChevronLeft, ChevronRight, Download, X, Copy, Check, FileText,
  ClipboardList, Hash, Calendar, FileSignature, Tag, Workflow, ArrowRightLeft,
  User, Users, KeyRound, CircleDollarSign, ShieldCheck, Code2, Fingerprint, Send,
  Receipt
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { StatusBadge } from "@/components/StatusBadge";
import { cn } from "@/lib/utils";

interface Props {
  notas: NotaSimplificada[];
  index: number | null;
  onClose: () => void;
  onChangeIndex: (i: number) => void;
}

export function NotaDetailModal({ notas, index, onClose, onChangeIndex }: Props) {
  const open = index !== null;
  const nota = open && index !== null ? notas[index] : null;

  // Tenta hidratar a nota sob demanda se ela não tiver itens
  useEffect(() => {
    if (open && nota && nota.itens.length === 0) {
      console.log(`[modal] Tentando hidratar nota ${nota.chave} sob demanda...`);
      import("@/lib/local/sync").then(m => m.hydrateSingleNota(nota.chave));
    }
  }, [open, nota?.chave]);
  const [loading, setLoading] = useState(false);
  const [cache] = useState(() => new Set<string>());
  const [copied, setCopied] = useState(false);

  const tableHoverRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!nota) return;
    if (cache.has(nota.chave)) { setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => { cache.add(nota.chave); setLoading(false); }, 120);
    return () => clearTimeout(t);
  }, [nota, cache]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();

      if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && tableHoverRef.current && scrollContainerRef.current) {
        e.preventDefault();
        const amount = 350; 
        if (e.key === "ArrowLeft") {
          scrollContainerRef.current.scrollBy({ left: -amount, behavior: "smooth" });
        } else {
          scrollContainerRef.current.scrollBy({ left: amount, behavior: "smooth" });
        }
        return;
      }

      if (e.key === "ArrowLeft" && index !== null && index > 0) onChangeIndex(index - 1);
      if (e.key === "ArrowRight" && index !== null && index < notas.length - 1) onChangeIndex(index + 1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, notas.length, onChangeIndex, onClose]);

  function copyChave() {
    if (!nota) return;
    navigator.clipboard.writeText(nota.chave).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  function exportarPDF() {
    if (!nota) return;

    const doc = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const m = 15;
    const contentW = pageWidth - (m * 2);

    const drawField = (x: number, currY: number, w: number, h: number, label: string, value: string | number, align: "left" | "right" | "center" = "left", bold = true, r = 3) => {
      doc.setDrawColor(0); 
      doc.setLineWidth(0.6);
      doc.roundedRect(x, currY, w, h, r, r);
      
      doc.setFontSize(5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80);
      doc.text(label.toUpperCase(), x + 3, currY + 7);
      
      doc.setFontSize(7);
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setTextColor(0);
      const textX = align === "right" ? x + w - 3 : align === "center" ? x + (w / 2) : x + 3;
      const valStr = String(value || "");
      const valY = currY + 17; 
      doc.text(valStr, textX, valY, { align, maxWidth: w - 5 });
    };

    const drawSectionTitle = (title: string, currY: number) => {
      doc.setFillColor(245, 245, 245);
      doc.setDrawColor(0);
      doc.setLineWidth(0.6);
      doc.roundedRect(m, currY - 15, contentW, 12, 2, 2, "FD");
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0);
      doc.text(title.toUpperCase(), m + 5, currY - 6.5);
    };

    const renderHeader = (pageNum: number, totalPages: number, initialY = m) => {
      let currY = initialY;
      doc.setDrawColor(0);

      const hH = 95;
      doc.roundedRect(m, currY, contentW * 0.42, hH, 3, 3);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(nota.emitente || "", m + (contentW * 0.21), currY + 20, { align: "center", maxWidth: contentW * 0.38 });
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      const endE = nota.detalhes?.emitente?.endereco;
      if (endE) {
        const line1 = [endE.xLgr, endE.nro, endE.xBairro].filter(Boolean).join(", ");
        const line2 = `CEP: ${endE.CEP || ""} - ${endE.xMun || ""} - ${endE.UF || ""}`;
        doc.text(line1 || "", m + (contentW * 0.21), currY + 45, { align: "center" });
        doc.text(line2 || "", m + (contentW * 0.21), currY + 55, { align: "center" });
        doc.text(`Fone/Fax: ${endE.fone || ""}`, m + (contentW * 0.21), currY + 65, { align: "center" });
      }

      doc.roundedRect(m + (contentW * 0.425), currY, contentW * 0.16, hH, 1, 1);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("DANFE", m + (contentW * 0.505), currY + 12, { align: "center" });
      
      doc.setFontSize(5);
      doc.setFont("helvetica", "bold");
      doc.text("DOCUMENTO AUXILIAR\nDA NOTA FISCAL\nELETRÔNICA", m + (contentW * 0.505), currY + 20, { align: "center" });

      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.text("0 - Entrada", m + (contentW * 0.435), currY + 44);
      doc.text("1 - Saída", m + (contentW * 0.435), currY + 52);
      
      const tpNF = (nota.detalhes?.ide as any)?.tpNF === "0" ? "0" : "1";
      doc.rect(m + (contentW * 0.53), currY + 39, 18, 16);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(tpNF, m + (contentW * 0.53) + 9, currY + 51, { align: "center" });

      doc.setFontSize(8);
      const colX1 = m + (contentW * 0.435);
      const colX2 = m + (contentW * 0.585);
      
      doc.setFont("helvetica", "bold");
      doc.text("Nº", colX1, currY + 70);
      doc.setFont("helvetica", "normal");
      doc.text(nota.numero || "", colX2, currY + 70, { align: "right" });
      
      doc.setFont("helvetica", "bold");
      doc.text("SÉRIE :", colX1, currY + 80);
      doc.setFont("helvetica", "normal");
      doc.text(nota.serie || "", colX2, currY + 80, { align: "right" });
      
      doc.setFont("helvetica", "bold");
      doc.text("FOLHA:", colX1, currY + 90);
      doc.setFont("helvetica", "normal");
      doc.text(`${pageNum} de ${totalPages}`, colX2, currY + 90, { align: "right" });

      doc.roundedRect(m + (contentW * 0.59), currY, contentW * 0.41, hH, 1, 1);
      doc.setFillColor("#000000");
      let bX = m + (contentW * 0.59) + 10;
      const bEndX = m + contentW - 10;
      while (bX < bEndX) {
        const bw = Math.random() * 2.5 + 0.8;
        if (bX + bw > bEndX) break;
        doc.rect(bX, currY + 5, bw, 33, "F");
        bX += bw + (Math.random() * 1.5 + 0.5);
      }
      drawField(m + (contentW * 0.59), currY + 40, contentW * 0.41, 26, "Chave de Acesso", formatChave(nota.chave), "left", true);
      doc.setFontSize(6);
      doc.text("Consulta de autenticidade no portal nacional da NF-e", m + (contentW * 0.59) + 5, currY + 74);
      doc.text("www.nfe.fazenda.gov.br/portal ou no site da Sefaz Autorizadora", m + (contentW * 0.59) + 5, currY + 82);

      currY += hH + 2;
      const prot = nota.detalhes?.protocolo as any;
      drawField(m, currY, contentW * 0.55, 22, "Natureza da Operação", (nota.natOp || "").slice(0, 70), "left");
      drawField(m + (contentW * 0.555), currY, contentW * 0.445, 22, "Protocolo de Autorização de Uso", (prot?.nProt || prot?.nProtocolo || "") + (prot?.dhRecbto ? " - " + formatDh(prot.dhRecbto) : ""), "left");

      currY += 24;
      drawField(m, currY, contentW * 0.25, 22, "Inscrição Estadual", nota.detalhes?.emitente?.IE || "", "left");
      drawField(m + (contentW * 0.255), currY, contentW * 0.25, 22, "Insc. Est. do Subst. Trib.", (nota.detalhes?.emitente as any)?.IEST || "", "left");
      drawField(m + (contentW * 0.51), currY, contentW * 0.49, 22, "CNPJ", nota.cnpjEmit, "left");

      return currY + 40;
    };

    const renderCanhoto = (y: number) => {
      const h = 35;
      const w1 = contentW * 0.84;
      const w2 = contentW * 0.15;
      const gap = contentW * 0.01;

      doc.setLineWidth(0.6);
      doc.setDrawColor(0);
      doc.roundedRect(m, y, w1, h, 2, 2);
      doc.setFontSize(6);
      doc.setFont("helvetica", "bold");
      doc.text(`RECEBEMOS DE ${nota.emitente || ""} OS PRODUTOS CONSTANTES DA NOTA FISCAL INDICADA AO LADO`, m + 3, y + 9, { maxWidth: w1 - 10 });
      
      doc.line(m, y + 14, m + w1, y + 14);
      doc.line(m + (w1 * 0.25), y + 14, m + (w1 * 0.25), y + h);
      
      doc.setFontSize(5);
      doc.setFont("helvetica", "normal");
      doc.text("DATA DE RECEBIMENTO", m + 3, y + 18.5);
      doc.text("IDENTIFICAÇÃO E ASSINATURA DO RECEBEDOR", m + (w1 * 0.25) + 3, y + 18.5);

      doc.roundedRect(m + w1 + gap, y, w2, h, 2, 2);
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.text("NF-e", m + w1 + gap + (w2/2), y + 10, { align: "center" });
      doc.setFontSize(8);
      doc.text(`Nº ${nota.numero}`, m + w1 + gap + (w2/2), y + 20, { align: "center" });
      doc.setFontSize(7);
      doc.text(`SÉRIE ${nota.serie}`, m + w1 + gap + (w2/2), y + 30, { align: "center" });

      (doc as any).setLineDash?.([2, 2]);
      doc.line(m, y + h + 8, m + contentW, y + h + 8);
      (doc as any).setLineDash?.([]);
      
      return y + h + 15;
    };

    let startY = renderCanhoto(15);
    startY = renderHeader(1, 1, startY);

    drawSectionTitle("DESTINATÁRIO / REMETENTE", startY);
    const dst = nota.detalhes?.destinatario;
    const ed = dst?.endereco;
    drawField(m, startY, contentW * 0.61, 22, "Nome / Razão Social", nota.destinatario, "left");
    drawField(m + (contentW * 0.615), startY, contentW * 0.21, 22, "CNPJ / CPF", nota.cnpjDest, "left");
    drawField(m + (contentW * 0.83), startY, contentW * 0.17, 22, "Data Emissão", dataEmissaoCurta, "right");
    startY += 25;
    drawField(m, startY, contentW * 0.48, 22, "Endereço", `${ed?.xLgr || ""}, ${ed?.nro || ""}`, "left");
    drawField(m + (contentW * 0.485), startY, contentW * 0.23, 22, "Bairro / Distrito", ed?.xBairro || "", "left");
    drawField(m + (contentW * 0.72), startY, contentW * 0.105, 22, "CEP", ed?.CEP || "", "left");
    drawField(m + (contentW * 0.83), startY, contentW * 0.17, 22, "Data Saída", dataEmissaoCurta, "right");
    startY += 25;
    drawField(m, startY, contentW * 0.36, 22, "Município", ed?.xMun || "", "left");
    drawField(m + (contentW * 0.365), startY, contentW * 0.045, 22, "UF", ed?.UF || "", "center");
    drawField(m + (contentW * 0.415), startY, contentW * 0.15, 22, "Fone / Fax", ed?.fone || "", "left");
    drawField(m + (contentW * 0.57), startY, contentW * 0.255, 22, "Inscrição Estadual", dst?.IE || "", "left");
    drawField(m + (contentW * 0.83), startY, contentW * 0.17, 22, "Hora Saída", nota.detalhes?.ide?.dhSaiEnt ? formatDh(nota.detalhes.ide.dhSaiEnt).split(" ")[1] : "00:00:00", "right");
    startY += 40;

    drawSectionTitle("FATURA / DUPLICATA", startY);
    const fatW = contentW / 3;
    drawField(m, startY, fatW - 2, 22, "Número", "001", "left");
    drawField(m + fatW, startY, fatW - 2, 22, "Vencimento", dataEmissaoCurta, "left");
    drawField(m + (fatW * 2), startY, fatW, 22, "Valor", fmtBRL(nota.valorOficial), "right");
    startY += 40;

    drawSectionTitle("CÁLCULO DO IMPOSTO", startY);
    const cW = contentW / 5;
    drawField(m, startY, cW - 2, 22, "Base Calc. ICMS", fmtNum(nota.vProd), "right");
    drawField(m + cW, startY, cW - 2, 22, "Valor ICMS", fmtBRL(nota.vICMS), "right");
    drawField(m + (cW * 2), startY, cW - 2, 22, "Base ICMS ST", "0,00", "right");
    drawField(m + (cW * 3), startY, cW - 2, 22, "Valor ICMS ST", fmtBRL(nota.vST), "right");
    drawField(m + (cW * 4), startY, cW, 22, "V. Total Produtos", fmtBRL(nota.vProd), "right");
    startY += 25;
    drawField(m, startY, cW - 2, 22, "Valor Frete", fmtBRL(nota.vFrete), "right");
    drawField(m + cW, startY, cW - 2, 22, "Valor Seguro", fmtBRL(nota.vSeg), "right");
    drawField(m + (cW * 2), startY, cW - 2, 22, "Desconto", fmtBRL(nota.vDesc), "right");
    drawField(m + (cW * 3), startY, cW - 2, 22, "Outras Despesas", fmtBRL(nota.vOutro), "right");
    drawField(m + (cW * 4), startY, cW, 22, "V. Total Nota", fmtBRL(nota.valorOficial), "right");
    startY += 25;
    const vTotTrib = (nota.detalhes?.total?.ICMSTot as any)?.vTotTrib;
    drawField(m, startY, contentW, 22, "Valor Aproximado dos Tributos (Lei 12.741/2012)", vTotTrib ? fmtBRL(vTotTrib) : "R$ 0,00", "right");
    startY += 40;

    drawSectionTitle("TRANSPORTADOR / VOLUMES TRANSPORTADOS", startY);
    drawField(m, startY, contentW * 0.4, 22, "Razão Social", (nota.emitente || "").slice(0, 50), "left");
    drawField(m + (contentW * 0.405), startY, contentW * 0.15, 22, "Frete por Conta", "0-Remetente", "left");
    drawField(m + (contentW * 0.56), startY, contentW * 0.15, 22, "Código ANTT", "", "left");
    drawField(m + (contentW * 0.715), startY, contentW * 0.12, 22, "Placa Veículo", "", "left");
    drawField(m + (contentW * 0.84), startY, contentW * 0.03, 22, "UF", "RJ", "center");
    drawField(m + (contentW * 0.875), startY, contentW * 0.125, 22, "CNPJ/CPF", nota.cnpjEmit, "left");
    startY += 25;
    const transp = (nota.detalhes?.transporte as any) || {};
    const tEnder = transp.transporta?.xEnder || "";
    const tMun = transp.transporta?.xMun || "";
    const tUF = transp.transporta?.UF || "";
    const tIE = transp.transporta?.IE || "";

    drawField(m, startY, contentW * 0.4, 22, "Endereço", tEnder, "left");
    drawField(m + (contentW * 0.405), startY, contentW * 0.35, 22, "Município", tMun, "left");
    drawField(m + (contentW * 0.76), startY, contentW * 0.04, 22, "UF", tUF, "center");
    drawField(m + (contentW * 0.805), startY, contentW * 0.195, 22, "Inscrição Estadual", tIE, "left");
    startY += 25;
    const vW = contentW / 6;
    drawField(m, startY, vW - 1, 22, "Quantidade", transp.vol?.qVol || "", "left");
    drawField(m + vW, startY, vW - 1, 22, "Espécie", transp.vol?.esp || "", "left");
    drawField(m + (vW * 2), startY, vW - 1, 22, "Marca", transp.vol?.marca || "", "left");
    drawField(m + (vW * 3), startY, vW - 1, 22, "Numeração", transp.vol?.nVol || "", "left");
    drawField(m + (vW * 4), startY, vW - 1, 22, "Peso Bruto", fmtNum(transp.vol?.pesoB || 0), "right");
    drawField(m + (vW * 5), startY, vW, 22, "Peso Líquido", fmtNum(transp.vol?.pesoL || 0), "right");
    startY += 40;

    drawSectionTitle("DADOS DOS PRODUTOS / SERVIÇOS", startY);

    autoTable(doc, {
      startY,
      theme: "grid",
      margin: { left: m, right: m },
      head: [["CÓDIGO PRODUTO", "DESCRIÇÃO DO PRODUTO / SERVIÇO", "NCM/SH", "O/CST", "CFOP", "UN", "QUANT", "VALOR UNIT", "VALOR TOTAL", "VALOR DESC", "B.CÁLC ICMS", "VALOR ICMS", "VALOR IPI", "ALÍQ. ICMS", "ALÍQ. IPI"]],
      body: nota.itens.map(it => [
        it.cProd,
        it.xProd + (it.infAdProd ? "\n" + it.infAdProd : ""),
        it.ncm, 
        (it.impostos?.icms?.orig || "0") + (it.impostos?.icms?.cst || (it.impostos?.icms as any)?.csosn || "00"),
        it.cfop, it.uCom,
        fmtNum(it.qCom), fmtNum(it.vUnCom), fmtNum(it.vProd), fmtNum(it.vDesc || 0),
        fmtNum(it.impostos?.icms?.vBC || 0), fmtNum(it.vICMS || 0), fmtNum(it.vIPI || 0),
        (it.impostos?.icms?.pICMS || 0) + "%", (it.impostos?.ipi?.pIPI || 0) + "%"
      ]),
      styles: { fontSize: 5, cellPadding: 2, lineColor: [120, 120, 120], textColor: [0, 0, 0], lineWidth: 0.4 },
      headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: "bold", halign: "center", lineWidth: 0.6, lineColor: [120, 120, 120] },
      columnStyles: {
        0: { cellWidth: 40 }, 1: { cellWidth: "auto" }, 2: { cellWidth: 35 },
        3: { cellWidth: 22 }, 4: { cellWidth: 22 }, 5: { cellWidth: 15 },
        6: { cellWidth: 28, halign: "right" }, 7: { cellWidth: 35, halign: "right" },
        8: { cellWidth: 40, halign: "right" }, 9: { cellWidth: 30, halign: "right" },
        10: { cellWidth: 35, halign: "right" }, 11: { cellWidth: 35, halign: "right" },
        12: { cellWidth: 30, halign: "right" }, 13: { cellWidth: 25, halign: "right" },
        14: { cellWidth: 25, halign: "right" }
      }
    });

    let finalY = (doc as any).lastAutoTable.finalY + 22;
    if (finalY > pageHeight - 100) { doc.addPage(); finalY = m + 22; }

    drawSectionTitle("DADOS ADICIONAIS", finalY);
    drawField(m, finalY, contentW * 0.68, 65, "Informações Complementares", (nota.infCpl || "").slice(0, 600), "left", false);
    drawField(m + (contentW * 0.69), finalY, contentW * 0.31, 65, "Reservado ao Fisco", "", "left");

    doc.save(`DANFE_${nota.numero}_${nota.serie}.pdf`);
  }

  const meta = useMemo(() => {
    if (!nota?.xmlOriginal) return { protocolo: "", dhRecbto: "", tpAmb: "", versao: "", tpEmis: "", digest: "" };
    const xml = nota.xmlOriginal;
    const grab = (re: RegExp) => xml.match(re)?.[1]?.trim() ?? "";
    return {
      protocolo: grab(/<nProt>([^<]+)<\/nProt>/),
      dhRecbto: grab(/<dhRecbto>([^<]+)<\/dhRecbto>/),
      tpAmb: grab(/<tpAmb>([^<]+)<\/tpAmb>/),
      versao: grab(/<infNFe[^>]*versao="([^"]+)"/),
      tpEmis: grab(/<tpEmis>([^<]+)<\/tpEmis>/),
      digest: grab(/<DigestValue>([^<]+)<\/DigestValue>/),
    };
  }, [nota]);

  if (!nota) return null;

  const ambiente = meta.tpAmb === "1" ? "Produção" : meta.tpAmb === "2" ? "Homologação" : "—";
  const tipoEmissao = meta.tpEmis === "1" ? "Normal" : meta.tpEmis ? `Autorizada em Contingência (${meta.tpEmis})` : "Normal";
  const dataEmissaoFmt = fmtDate(nota.emissao);
  const dataEmissaoCurta = fmtDate(nota.emissao);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[97vw] w-[1400px] max-h-[95vh] overflow-y-auto p-0 border border-foreground/10 shadow-2xl bg-background/95 backdrop-blur-3xl !rounded-2xl custom-scrollbar">
        {/* HEADER */}
        <DialogHeader className="px-6 pt-4 pb-3 border-b border-foreground/5 sticky top-0 z-20 bg-background/80 backdrop-blur-xl">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex items-start gap-4">
              <div className="size-14 rounded-2xl grid place-items-center bg-primary/10 border border-primary/20 shadow-[0_4px_18px_rgba(255,122,0,0.2)] shrink-0">
                <Receipt className="size-6 text-primary" />
              </div>
              <div className="min-w-0 pt-0.5">
                <DialogTitle className="text-xl flex items-center gap-2 flex-wrap font-black text-foreground">
                  <span>NF-{nota.modelo} nº</span>
                  <span className="font-black text-primary drop-shadow-[0_0_8px_rgba(255,122,0,0.4)]">{nota.numero}</span>
                  <span className="opacity-60 font-bold text-sm">/ série {nota.serie}</span>
                  <StatusBadge status={nota.notaStatus} size="sm" className="ml-1.5" />
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-1 font-bold uppercase tracking-widest">Emitido em {dataEmissaoFmt}</p>
                <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2">
                  <span className="text-[9px] font-black text-muted-foreground uppercase tracking-[0.2em] bg-muted/10 px-2 py-0.5 rounded border border-foreground/5">Chave de Acesso</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] tracking-widest font-bold text-foreground">{formatChave(nota.chave)}</span>
                    <button onClick={copyChave} className="text-muted-foreground hover:text-primary transition-colors p-1" title="Copiar Chave">
                      {copied ? <Check className="size-3.5 text-emerald-500" /> : <Copy className="size-3.5" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => onChangeIndex(index! - 1)} disabled={index === 0} className="btn-glass text-foreground h-8 px-3">
                <ChevronLeft className="size-4" />
              </Button>
              <span className="text-[10px] font-black uppercase tracking-widest text-foreground/80 tabular-nums px-4 py-1.5 glass rounded-md border-foreground/5">{(index ?? 0) + 1} / {notas.length}</span>
              <Button variant="outline" size="sm" onClick={() => onChangeIndex(index! + 1)} disabled={index === notas.length - 1} className="btn-glass text-foreground h-8 px-3">
                <ChevronRight className="size-4" />
              </Button>
              <div className="w-px h-6 bg-foreground/10 mx-2"></div>
              <Button variant="outline" size="sm" onClick={exportarPDF} className="btn-glass text-foreground h-8 px-4 text-[10px] font-black uppercase tracking-widest">
                <Download className="size-3.5 mr-2" /> PDF
              </Button>

              <Button variant="ghost" size="icon" onClick={onClose} className="ml-2 rounded-full hover:bg-foreground/5 text-foreground/60 hover:text-foreground transition-colors">
                <X className="size-5" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 py-4 min-h-[50vh]">
          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-[120px] w-full rounded-2xl bg-muted/10" />
              <div className="grid grid-cols-12 gap-4">
                <Skeleton className="h-[200px] col-span-3 rounded-2xl bg-muted/10" />
                <Skeleton className="h-[200px] col-span-3 rounded-2xl bg-muted/10" />
                <Skeleton className="h-[200px] col-span-3 rounded-2xl bg-muted/10" />
                <Skeleton className="h-[200px] col-span-3 rounded-2xl bg-muted/10" />
              </div>
              <Skeleton className="h-[400px] w-full rounded-2xl bg-muted/10" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* DADOS GERAIS - Condensed Grid */}
              <Block title="Visão Geral do Documento" titleIcon={<ClipboardList className="size-4" />}>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
                  <PillField icon={<Tag className="size-4" />} label="Modelo" value={String(nota.modelo)} />
                  <PillField icon={<ClipboardList className="size-4" />} label="Série" value={nota.serie || "—"} />
                  <PillField icon={<Hash className="size-4" />} label="Número" value={nota.numero || "—"} />
                  <PillField icon={<Calendar className="size-4" />} label="Data de Emissão" value={dataEmissaoCurta} />
                  <PillField
                    icon={<FileSignature className="size-4" />}
                    label="Status Sefaz"
                    custom={<StatusBadge status={nota.notaStatus} size="sm" />}
                  />
                  <PillField icon={<ArrowRightLeft className="size-4" />} label="Operação" value={(nota.detalhes?.ide as any)?.tpNF === "0" ? "Entrada" : "Saída"} />
                  <PillField icon={<Workflow className="size-4" />} label="Finalidade" value="Normal" />
                  <PillField icon={<Workflow className="size-4" />} label="Natureza" value={nota.natOp || "—"} />
                </div>
              </Block>

              {/* LINHA: EMITENTE | DESTINATÁRIO | CHAVE | TOTAIS */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-3">
                  <PartyBlock
                    icon={<User className="size-4 text-blue-500" />}
                    title="EMITENTE"
                    nome={nota.emitente}
                    cnpj={nota.cnpjEmit}
                    uf={nota.ufEmit}
                  />
                </div>
                <div className="lg:col-span-3">
                  <PartyBlock
                    icon={<Users className="size-4 text-emerald-500" />}
                    title="DESTINATÁRIO"
                    nome={nota.destinatario}
                    cnpj={nota.cnpjDest}
                    uf={nota.ufDest}
                  />
                </div>
                <div className="lg:col-span-3">
                  <Block title="Validação Sistêmica" titleIcon={<KeyRound className="size-4" />}>
                    <div className="flex flex-col gap-2">
                      <div className="glass bg-muted/5 p-3 rounded-xl border-foreground/5 text-center">
                        <div className="text-[9px] uppercase font-black tracking-widest text-muted-foreground mb-2">Assinatura Digital / Chave</div>
                        <div className="font-mono text-[10px] tracking-widest break-all text-foreground font-bold">
                          {formatChave(nota.chave)}
                        </div>
                      </div>
                      <PillField icon={<Send className="size-4" />} label="Ambiente Sefaz" value={ambiente} colSpan />
                      <PillField icon={<Code2 className="size-4" />} label="Versão XML" value={meta.versao || "—"} colSpan />
                    </div>
                  </Block>
                </div>
                <div className="lg:col-span-3">
                  <Block title="TOTAIS DO DOCUMENTO" titleIcon={<CircleDollarSign className="size-4" />}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <TotalCard label="Produtos" value={nota.vProd} />
                      <TotalCard label="Total (vNF)" value={nota.valorOficial} highlight />
                      <TotalCard label="ICMS" value={nota.vICMS} />
                      <TotalCard label="ICMS ST" value={nota.vST} />
                      <TotalCard label="IPI" value={nota.vIPI} />
                      <TotalCard label="PIS" value={nota.vPIS} />
                      <TotalCard label="COFINS" value={nota.vCOFINS} />
                      <TotalCard label="Outros" value={nota.vOutro} />
                    </div>
                  </Block>
                </div>
              </div>

              {/* IMPOSTOS (abas) + METADADOS */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-12">
                  <Block compact>
                    <Tabs defaultValue="itens" className="w-full">
                      <div className="px-4 pt-3 pb-0 border-b border-foreground/5 bg-muted/5">
                        <TabsList className="bg-transparent border-none p-0 flex gap-4">
                          <TabsTrigger value="itens" className="rounded-none border-b-2 border-transparent text-xs font-black uppercase tracking-widest px-1 py-3 text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:bg-transparent transition-all">
                            Itens e Impostos ({nota.itens.length})
                          </TabsTrigger>
                          <TabsTrigger value="impostos" className="rounded-none border-b-2 border-transparent text-xs font-black uppercase tracking-widest px-1 py-3 text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:bg-transparent transition-all">
                            Resumo de Tributos
                          </TabsTrigger>
                          <TabsTrigger value="contabil" className="rounded-none border-b-2 border-transparent text-xs font-black uppercase tracking-widest px-1 py-3 text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:bg-transparent transition-all">
                            Dados Consolidados
                          </TabsTrigger>
                        </TabsList>
                      </div>

                      <TabsContent value="itens" className="p-0 m-0">
                        <div
                          ref={scrollContainerRef}
                          onMouseEnter={() => { tableHoverRef.current = true; }}
                          onMouseLeave={() => { tableHoverRef.current = false; }}
                          className="overflow-x-auto bg-transparent custom-scrollbar max-h-[calc(90vh-420px)] min-h-[250px] overflow-y-auto"
                        >
                          <Table wrapperClassName="overflow-visible" className="min-w-[2000px] whitespace-nowrap">
                            <TableHeader className="sticky top-0 bg-background/90 backdrop-blur-md z-10 border-b border-foreground/5 shadow-sm">
                              <TableRow className="hover:bg-transparent border-none">
                                <TableHead className="w-[40px] text-center font-black text-[9px] uppercase tracking-widest text-muted-foreground px-4 py-4 border-r border-foreground/5">#</TableHead>
                                <TableHead className="font-black text-[9px] uppercase tracking-widest text-muted-foreground px-4 py-4 border-r border-foreground/5 min-w-[280px]">Produto / Serviço</TableHead>
                                <TableHead className="font-black text-[9px] uppercase tracking-widest text-muted-foreground px-4 py-4 border-r border-foreground/5">CFOP / NCM</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-widest text-muted-foreground px-4 py-4 border-r border-foreground/5">Qtd / UN</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-widest text-muted-foreground px-4 py-4 border-r border-foreground/5">Valor Unitário</TableHead>

                                <TableHead className="text-right font-black text-[9px] uppercase tracking-widest text-blue-500 bg-blue-500/5 border-r border-foreground/5">ICMS</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-widest text-cyan-500 bg-cyan-500/5 border-r border-foreground/5">ICMS-ST</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-widest text-indigo-500 bg-indigo-500/5 border-r border-foreground/5">FCP</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-widest text-violet-500 bg-violet-500/5 border-r border-foreground/5">FCP-ST</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-widest text-purple-500 bg-purple-500/5 border-r border-foreground/5">IPI</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-widest text-amber-500 bg-amber-500/5 border-r border-foreground/5">PIS</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-widest text-orange-500 bg-orange-500/5 border-r border-foreground/5">COFINS</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-widest text-pink-500 bg-pink-500/5 border-r border-foreground/5">ISSQN</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-widest text-rose-500 bg-rose-500/5 border-r border-foreground/5">IBS</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-widest text-red-500 bg-red-500/5 border-r border-foreground/5">CBS</TableHead>

                                <TableHead className="text-right font-black text-[9px] uppercase tracking-widest text-emerald-500 bg-emerald-500/5">Valor Total</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody className="divide-y divide-foreground/5">
                              {nota.itens.length === 0 && (
                                <TableRow><TableCell colSpan={16} className="text-center text-muted-foreground text-[10px] font-black uppercase tracking-widest py-12">No items found.</TableCell></TableRow>
                              )}
                              {nota.itens.map((it) => (
                                <TableRow key={it.nItem} className="hover:bg-primary/[0.03] transition-colors group">
                                  <TableCell className="text-[10px] text-muted-foreground font-black text-center border-r border-foreground/5">{it.nItem}</TableCell>
                                  <TableCell className="max-w-[280px] px-4 py-3 border-r border-foreground/5">
                                    <div className="font-bold text-xs text-foreground truncate" title={it.xProd}>{it.xProd}</div>
                                    <div className="text-[10px] text-muted-foreground font-mono mt-1 truncate font-bold">CODE: {it.cProd}</div>
                                  </TableCell>
                                  <TableCell className="px-4 py-3 border-r border-foreground/5">
                                    <div className="text-[10px] font-mono font-bold text-primary bg-primary/10 px-2 py-0.5 rounded inline-block">{it.cfop}</div>
                                    <div className="text-[9px] text-muted-foreground font-mono mt-1.5 font-bold tracking-widest">NCM: {it.ncm}</div>
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums text-xs font-bold text-foreground px-4 py-3 border-r border-foreground/5">
                                    {fmtNum(it.qCom)} <span className="text-[9px] text-muted-foreground uppercase ml-1 tracking-widest">{it.uCom}</span>
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums text-xs font-bold text-muted-foreground px-4 py-3 border-r border-foreground/5">{fmtBRL(it.vUnCom)}</TableCell>

                                  <TableCell className="text-right bg-blue-500/5 group-hover:bg-blue-500/10 transition-colors border-l border-foreground/5">
                                    <div className="tabular-nums text-xs font-bold text-blue-500">{fmtBRL(it.vICMS || 0)}</div>
                                    <div className="text-[9px] font-mono text-blue-500/70 mt-1 font-bold tracking-widest">
                                      CST {it.impostos?.icms?.cst || (it.impostos?.icms as any)?.csosn || "—"}
                                      {it.impostos?.icms?.pICMS != null ? ` • ${it.impostos.icms.pICMS}%` : ""}
                                    </div>
                                    {it.impostos?.icms?.vBC != null && it.impostos.icms.vBC !== it.vProd && (
                                      <div className="text-[8px] font-mono text-blue-500/50 mt-0.5 font-bold">BC {fmtBRL(it.impostos.icms.vBC)}</div>
                                    )}
                                  </TableCell>

                                  <TableCell className="text-right bg-cyan-500/5 group-hover:bg-cyan-500/10 transition-colors">
                                    <div className="tabular-nums text-xs font-bold text-cyan-500">{fmtBRL(it.impostos?.icmsST?.vICMSST || 0)}</div>
                                    <div className="text-[9px] font-mono text-cyan-500/70 mt-1 font-bold tracking-widest">{it.impostos?.icmsST?.pICMSST ? `${it.impostos.icmsST.pICMSST}%` : "—"}</div>
                                  </TableCell>

                                  <TableCell className="text-right bg-indigo-500/5 group-hover:bg-indigo-500/10 transition-colors border-r border-foreground/5">
                                    <div className="tabular-nums text-xs font-bold text-indigo-500">{fmtBRL(it.impostos?.fcp?.vFCP || 0)}</div>
                                    {it.impostos?.fcp?.pFCP != null && (
                                      <div className="text-[9px] font-mono text-indigo-500/70 mt-1 font-bold tracking-widest">{it.impostos.fcp.pFCP}%</div>
                                    )}
                                  </TableCell>

                                  <TableCell className="text-right bg-violet-500/5 group-hover:bg-violet-500/10 transition-colors border-r border-foreground/5">
                                    <div className="tabular-nums text-xs font-bold text-violet-500">{fmtBRL(it.impostos?.fcpST?.vFCPST || 0)}</div>
                                    {it.impostos?.fcpST?.pFCPST != null && (
                                      <div className="text-[9px] font-mono text-violet-500/70 mt-1 font-bold tracking-widest">{it.impostos.fcpST.pFCPST}%</div>
                                    )}
                                  </TableCell>

                                  <TableCell className="text-right bg-purple-500/5 group-hover:bg-purple-500/10 transition-colors">
                                    <div className="tabular-nums text-xs font-bold text-purple-500">{fmtBRL(it.vIPI || 0)}</div>
                                    <div className="text-[9px] font-mono text-purple-500/70 mt-1 font-bold tracking-widest">
                                      CST {it.impostos?.ipi?.cst || "—"}
                                      {it.impostos?.ipi?.pIPI != null ? ` • ${it.impostos.ipi.pIPI}%` : ""}
                                    </div>
                                  </TableCell>

                                  <TableCell className="text-right bg-amber-500/5 group-hover:bg-amber-500/10 transition-colors">
                                    <div className="tabular-nums text-xs font-bold text-amber-500">{fmtBRL(it.vPIS || 0)}</div>
                                    <div className="text-[9px] font-mono text-amber-500/70 mt-1 font-bold tracking-widest">
                                      CST {it.impostos?.pis?.cst || "—"}
                                      {it.impostos?.pis?.pPIS != null ? ` • ${it.impostos.pis.pPIS}%` : ""}
                                    </div>
                                  </TableCell>

                                  <TableCell className="text-right bg-orange-500/5 group-hover:bg-orange-500/10 transition-colors">
                                    <div className="tabular-nums text-xs font-bold text-orange-500">{fmtBRL(it.vCOFINS || 0)}</div>
                                    <div className="text-[9px] font-mono text-orange-500/70 mt-1 font-bold tracking-widest">
                                      CST {it.impostos?.cofins?.cst || "—"}
                                      {it.impostos?.cofins?.pCOFINS != null ? ` • ${it.impostos.cofins.pCOFINS}%` : ""}
                                    </div>
                                  </TableCell>

                                  <TableCell className="text-right bg-pink-500/5 group-hover:bg-pink-500/10 transition-colors">
                                    <div className="tabular-nums text-xs font-bold text-pink-500">{fmtBRL(it.impostos?.issqn?.vISSQN || 0)}</div>
                                  </TableCell>

                                  <TableCell className="text-right bg-rose-500/5 group-hover:bg-rose-500/10 transition-colors border-r border-foreground/5">
                                    <div className="tabular-nums text-xs font-bold text-rose-500">{fmtBRL(it.impostos?.ibsCbs?.vIBS || 0)}</div>
                                    {it.impostos?.ibsCbs?.pIBSUF != null && (
                                      <div className="text-[9px] font-mono text-rose-500/70 mt-1 font-bold tracking-widest">{it.impostos.ibsCbs.pIBSUF}%</div>
                                    )}
                                  </TableCell>

                                  <TableCell className="text-right bg-red-500/5 group-hover:bg-red-500/10 transition-colors border-r border-foreground/5">
                                    <div className="tabular-nums text-xs font-bold text-red-500">{fmtBRL(it.impostos?.ibsCbs?.vCBS || 0)}</div>
                                    {it.impostos?.ibsCbs?.pCBS != null && (
                                      <div className="text-[9px] font-mono text-red-500/70 mt-1 font-bold tracking-widest">{it.impostos.ibsCbs.pCBS}%</div>
                                    )}
                                  </TableCell>

                                  <TableCell className="text-right tabular-nums text-sm font-black text-emerald-500 border-l border-foreground/5">
                                    {fmtBRL(it.vProd)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </TabsContent>

                      <TabsContent value="impostos" className="p-6 m-0">
                        <div className="rounded-xl border border-foreground/5 overflow-hidden glass shadow-sm">
                          <Table>
                            <TableHeader className="bg-muted/10 border-b border-foreground/5">
                              <TableRow className="hover:bg-transparent border-none">
                                <TableHead className="font-black text-[9px] uppercase tracking-widest text-muted-foreground px-6 py-4 border-r border-foreground/5">Imposto</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-widest text-muted-foreground px-6 py-4 border-r border-foreground/5">Base de Cálculo</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-widest text-muted-foreground px-6 py-4 border-r border-foreground/5">Alíquota Média</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-widest text-muted-foreground px-6 py-4">Valor Total</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody className="divide-y divide-foreground/5">
                              {nota.impostosDetalhe.length === 0 && (
                                <TableRow><TableCell colSpan={4} className="text-center text-[10px] font-black uppercase tracking-widest text-muted-foreground py-12">Nenhum imposto calculado.</TableCell></TableRow>
                              )}
                              {nota.impostosDetalhe.map((i, idx) => (
                                <TableRow key={idx} className="hover:bg-primary/5 transition-all">
                                  <TableCell className="font-bold text-foreground px-6 py-4 border-r border-foreground/5">{i.sigla}</TableCell>
                                  <TableCell className="text-right tabular-nums font-bold text-muted-foreground px-6 py-4 border-r border-foreground/5">{fmtBRL(i.base)}</TableCell>
                                  <TableCell className="text-right tabular-nums text-muted-foreground px-6 py-4 border-r border-foreground/5">
                                    <span className="bg-muted/10 px-2 py-1 rounded text-[10px] font-black tracking-widest">{i.aliquota.toFixed(2)}%</span>
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums font-black text-sm text-foreground px-6 py-4">{fmtBRL(i.valor)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </TabsContent>

                      <TabsContent value="contabil" className="p-6 m-0">
                        <div className="rounded-2xl border border-foreground/5 p-6 space-y-6 glass shadow-sm">
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <PillField icon={<CircleDollarSign className="size-4" />} label="Valor Consolidado" value={fmtBRL(nota.valorOficial)} />
                            <PillField icon={<CircleDollarSign className="size-4" />} label="Valor Declarado (vNF)" value={nota.valorDeclarado != null ? fmtBRL(nota.valorDeclarado) : "—"} />
                            <PillField icon={<CircleDollarSign className="size-4" />} label="Soma dos Itens (vProd)" value={fmtBRL(nota.valorItens)} />
                            <PillField icon={<Tag className="size-4" />} label="Fonte" value={nota.fonte} />
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <PillField
                              icon={<ShieldCheck className="size-4" />}
                              label="Protocolo de Aut. Sefaz"
                              value={meta.protocolo || "—"}
                              sub={meta.dhRecbto ? formatDh(meta.dhRecbto) : ""}
                            />
                            <PillField icon={<Fingerprint className="size-4" />} label="Valor do Hash (Digest)" value={meta.digest || "—"} />
                          </div>

                          {nota.infCpl && (
                            <div className="pt-6 mt-4 border-t border-foreground/5">
                              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                                <FileText className="size-3" /> Informações Complementares
                              </p>
                              <div className="bg-amber-500/10 border border-amber-500/20 p-5 rounded-xl text-xs whitespace-pre-wrap text-amber-500 font-bold leading-relaxed shadow-inner">
                                {nota.infCpl}
                              </div>
                            </div>
                          )}
                        </div>
                      </TabsContent>
                    </Tabs>
                  </Block>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function formatChave(c: string) {
  if (!c) return "—";
  return c.replace(/(.{4})/g, "$1 ").trim();
}

function formatDh(s: string) {
  try {
    const dt = new Date(s);
    if (isNaN(dt.getTime())) return s;
    return dt.toLocaleString("pt-BR");
  } catch { return s; }
}

/* =========================================================
   Componentes visuais — pílulas e blocos translúcidos
   ========================================================= */

function Block({
  title, titleIcon, children, compact,
}: {
  title?: string; titleIcon?: React.ReactNode; children: React.ReactNode; compact?: boolean;
}) {
  return (
    <section className={cn("glass-card", compact ? "p-0 overflow-hidden" : "p-4")}>
      {title && (
        <div className="flex items-center gap-2 mb-3 text-primary">
          {titleIcon}
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-black">{title}</h3>
        </div>
      )}
      {children}
    </section>
  );
}

function PillField({
  icon, label, value, sub, custom, colSpan,
}: {
  icon?: React.ReactNode;
  label: string;
  value?: string;
  sub?: string;
  custom?: React.ReactNode;
  colSpan?: boolean;
}) {
  return (
    <div className={cn("glass group px-3 py-2 flex items-center gap-3 border border-foreground/5 hover:border-primary/30 transition-colors rounded-xl shadow-sm", colSpan && "col-span-2")}>
      {icon && (
        <div className="size-7 shrink-0 rounded-lg flex items-center justify-center bg-foreground/5 border border-foreground/10 text-foreground/70 group-hover:text-primary transition-colors shadow-sm">
          {icon}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[8px] uppercase font-black tracking-widest text-muted-foreground leading-tight mb-0.5">{label}</p>
        {custom ? (
          <div className="mt-0.5">{custom}</div>
        ) : (
          <p className="text-xs font-bold text-foreground leading-tight truncate" title={value}>{value || "—"}</p>
        )}
        {sub && <p className="text-[9px] font-bold text-muted-foreground mt-0.5 truncate">{sub}</p>}
      </div>
    </div>
  );
}

function PartyBlock({
  icon, title, nome, cnpj, uf,
}: {
  icon: React.ReactNode; title: string; nome: string; cnpj: string; uf: string;
}) {
  return (
    <Block title={title} titleIcon={icon}>
      <div className="space-y-3">
        <div className="min-h-[40px]">
          <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-0.5">Nome / Razão Social</p>
          <p className="font-bold text-foreground leading-snug text-[13px] line-clamp-2">{nome || "—"}</p>
        </div>
        <div className="flex items-center justify-between gap-2 pt-2 border-t border-foreground/5">
          <div className="min-w-0 flex-1">
            <p className="text-[8px] font-black uppercase tracking-widest text-muted-foreground mb-0.5">CNPJ</p>
            <p className="font-mono font-bold text-foreground text-[10px] truncate">{cnpj || "—"}</p>
          </div>
          <div className="shrink-0 text-right">
            <p className="text-[8px] font-black uppercase tracking-widest text-muted-foreground mb-0.5">UF</p>
            <p className="font-bold text-foreground text-[10px]">{uf || "—"}</p>
          </div>
        </div>
      </div>
    </Block>
  );
}

function TotalCard({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div
      className={cn("px-3 py-2 flex justify-between items-center rounded-xl border transition-colors",
        highlight
          ? "border-primary/50 bg-primary/10 shadow-[0_0_15px_rgba(255,122,0,0.1)]"
          : "glass border-foreground/5 shadow-sm"
      )}
    >
      <p className={cn("text-[9px] font-black uppercase tracking-widest leading-tight", highlight ? "text-primary" : "text-muted-foreground")}>
        {label}
      </p>
      <p className={cn("tabular-nums font-black", highlight ? "text-base text-primary drop-shadow-[0_0_8px_rgba(255,122,0,0.3)]" : "text-[13px] text-foreground")}>
        {fmtBRL(value)}
      </p>
    </div>
  );
}
